import { useCircleAIShellStore } from "@circle-react-uikit/CircleAIAppShell/store/circleAIShellStore";
import { paywallRoutes } from "@circle-react/lib/routes/paywalls";
import { useEffect, useLayoutEffect, useRef } from "react";

import { useCircleAiStore } from "@/react/components/CircleAi/useCircleAiStore";

interface UseRouteOwnedShellStateProps {
  isAdmin: boolean;
  isAdminArea: boolean;
  isAdminOrModerator: boolean;
  isAiRoute: boolean;
  isFullPageAiRoute: boolean;
  isShellBypassed: boolean;
  pathname: string;
}

export const useRouteOwnedShellState = ({
  isAdmin,
  isAdminArea,
  isAdminOrModerator,
  isFullPageAiRoute,
  isAiRoute,
  isShellBypassed,
  pathname,
}: UseRouteOwnedShellStateProps) => {
  const setMode = useCircleAIShellStore(s => s.setMode);
  const userMode = useCircleAIShellStore(s => s.userMode);
  const clearAnalyticsReport = useCircleAiStore(s => s.clearAnalyticsReport);
  // Moderators get admin shell mode on settings routes only (not AI routes).
  const isAdminShellRoute =
    (isAdmin && isAiRoute) || (isAdminOrModerator && isAdminArea);

  // This is the single lifecycle owner for route-derived shell mode:
  // - seed the persisted user preference on ordinary routes
  // - force admin mode on admin-owned routes
  // - restore the persisted preference when leaving those routes
  //
  // Keeping this in the shell prevents child/parent effect ordering from
  // changing the result between Strict Mode development and production.
  const didSeedModeRef = useRef(false);
  const previousIsAdminShellRouteRef = useRef<boolean | null>(null);
  const previousPathnameRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (isShellBypassed && !isAiRoute) return;

    const mode = useCircleAIShellStore.getState().mode;
    const wasInAdminShellRoute = previousIsAdminShellRouteRef.current;
    previousIsAdminShellRouteRef.current = isAdminShellRoute;

    if (isAdminShellRoute) {
      didSeedModeRef.current = true;
      if (mode !== "admin") setMode("admin");
      return;
    }

    const restoredMode = userMode ?? "community";
    const shouldRestoreMode =
      !didSeedModeRef.current || wasInAdminShellRoute === true;
    didSeedModeRef.current = true;

    if (shouldRestoreMode && mode !== restoredMode) {
      setMode(restoredMode);
    }
  }, [isShellBypassed, isAiRoute, isAdminShellRoute, userMode, setMode]);

  // Keep route-owned shell geometry here so child mount order cannot
  // overwrite it. Full-page AI routes keep the side panel closed;
  // other destinations (including projects) preserve an open chat but
  // collapse fullscreen back to the side panel so the destination remains
  // visible.
  useLayoutEffect(() => {
    const shellStore = useCircleAIShellStore.getState();
    const shouldPreserveConversationPanelState =
      pathname === paywallRoutes.onboarding();
    const isLeavingOnboarding =
      previousPathnameRef.current === paywallRoutes.onboarding();
    // "Collapse when the user navigates elsewhere" — not "fullscreen is never
    // allowed here". Without this the effect also fires on mount and on any
    // non-navigation re-run, which overwrites a fullscreen the URL asked for.
    const hasNavigated =
      previousPathnameRef.current !== null &&
      previousPathnameRef.current !== pathname;
    previousPathnameRef.current = pathname;

    if (isAiRoute) {
      shellStore.setAdminDockForced(false);
    }

    if (isFullPageAiRoute) {
      if (shellStore.conversationPanelState !== "closed") {
        shellStore.setConversationPanelState("closed");
      }
      return;
    }

    if (
      hasNavigated &&
      !shouldPreserveConversationPanelState &&
      !isLeavingOnboarding &&
      shellStore.conversationPanelState === "fullscreen"
    ) {
      shellStore.setConversationPanelState("default");
    }
  }, [pathname, isFullPageAiRoute, isAiRoute]);

  useEffect(() => {
    clearAnalyticsReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on navigation only
  }, [pathname]);
};
